import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useOnboarding } from "../../context/OnboardingContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { ApiHelper } from "../../utils/ApiHelper";
import { INVITE_DETAIL, AUTH_REGISTER_WITH_COGNITO } from "../../lib/API_KEYS";

export default function AcceptInviteForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const fullNameRef = useRef(null);
  const passwordRef = useRef(null);

  const { inviteData, setInviteData } = useOnboarding();

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  console.log(id);

  // ✅ Password Validation
  const isPasswordValid = (password) =>
    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(password);

  const validate = () => {
    return fullName.trim().length >= 3 && isPasswordValid(password);
  };

  // ✅ Fetch Invite
  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await new ApiHelper().FetchFromServer(
          INVITE_DETAIL[0].replace(":inviteId", id),
          INVITE_DETAIL[1],
          undefined,
          undefined,
          { Authorization: "Bearer" } // No Auth needed
        );
        const invite = res.data;
        setInviteData(invite);
        setFullName(invite.name || "");
      } catch (err) {
        console.error("Invalid invite:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [id, setInviteData]);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    const error = !isPasswordValid(value)
      ? "Password must be at least 8 characters and include a letter, a number, and a special character."
      : "";
    setPasswordError(error);
    setPassword(value);
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setPasswordError(
        !isPasswordValid(password)
          ? "Password must be at least 8 characters and include a letter, a number, and a special character."
          : ""
      );
      return;
    }

    setSubmitting(true);
    try {
      await new ApiHelper().FetchFromServer(
        AUTH_REGISTER_WITH_COGNITO[0],
        AUTH_REGISTER_WITH_COGNITO[1],
        undefined,
        {
          email: inviteData.email,
          password,
          name: fullName,
          role: inviteData.role,
          companyName: inviteData.companyName || "",
        },
        { Authorization: "Bearer" }
      );

      // Save in Context
      setInviteData({
        ...inviteData,
        fullName,
        password,
      });
      
      navigate("/invite-email-verification");
    } catch (err) {
      console.error("Signup error:", err);
      setPasswordError(err.message || "Failed to create account.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef?.current) {
        nextRef.current.focus();
      } else {
        handleSubmit(e);
      }
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!inviteData) return <div>Invite not found.</div>;

  return (
    <div className="d-flex vh-100">
      <div className="flex-grow-1 d-flex justify-content-center align-items-center position-relative">
        <div
          className="d-flex flex-column justify-content-center align-items-center px-4 py-5 position-relative w-100"
          style={{ maxWidth: "400px", margin: "0 auto", minHeight: "100vh" }}
        >
          <div className="w-100 d-flex flex-column align-items-center flex-grow-1 justify-content-center">
            {/* Logo */}
            <div className="text-center mb-4">
              <img src="/images/Logo.png" alt="Logo" style={{ height: "60px" }} />
            </div>
            <h3 className="fw-bold text-center mb-4">Complete Your Account</h3>

            <form onSubmit={handleSubmit} className="w-100">

              <div className="mb-3">
                <label className="form-label fw-semibold mb-2">Email</label>
                <input
                  type="email"
                  className="form-control border-2 custom-focus"
                  value={inviteData.email}
                  disabled
                />
              </div>

              <div className="mb-3">
                <label htmlFor="fullName" className="form-label fw-semibold mb-2">
                  Full name
                </label>
                <input
                  ref={fullNameRef}
                  type="text"
                  className="form-control border-2 custom-focus"
                  id="fullName"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  onBlur={() => handleBlur("fullName")}
                  onKeyDown={(e) => handleKeyDown(e, passwordRef)}
                />
              </div>

              {/* Password */}
              <div className="mb-3">
                <label
                  htmlFor="password"
                  className={`form-label fw-semibold mb-2 ${passwordError ? "text-danger" : ""}`}
                >
                  Enter password
                </label>
                <div className="input-icon-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    ref={passwordRef}
                    className={`form-control border-2 custom-focus ${passwordError ? "input-error" : ""}`}
                    id="password"
                    placeholder="At least 8 characters with letter, number"
                    value={password}
                    onChange={handlePasswordChange}
                    onBlur={() => handleBlur("password")}
                    onKeyDown={(e) => handleKeyDown(e, null)}
                  />
                  <span
                    className="position-absolute end-0 top-50 translate-middle-y me-2"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                  </span>
                </div>
                {passwordError && (
                  <div className="alert-box mt-2">
                    <img src="/images/security.svg" alt="Info" width={20} height={20} />
                    <div className="flex-grow-1">
                      <div className="fw-bold text-dark">Error</div>
                      <div>{passwordError}</div>
                    </div>
                    <span className="close-icon" onClick={() => setPasswordError("")}>
                      ❌
                    </span>
                  </div>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="btn btn-primary btn-md w-100 mt-3"
                style={{ backgroundColor: "#181D4C", height: "40px" }}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Create Account"}
              </button>

              {/* Terms */}
              <p className="text-muted text-center mt-4 small">
                By proceeding, you agree to the <br />
                <span className="text-dark text-decoration-underline">Terms of Privacy</span> and{" "}
                <span className="text-dark text-decoration-underline">Policy</span>.
              </p>
            </form>
          </div>

          {/* Back to login */}
          <p className="position-absolute start-0 end-0 bottom-0 text-center mb-3">
            <Link to="/login" className="text-dark text-decoration-underline">
              ← Back to Login
            </Link>
          </p>
        </div>
      </div>

      {/* ✅ Right Image (like signup) */}
      <div
        className="d-none d-md-flex align-items-center justify-content-center"
        style={{ width: "50%" }}
      >
        <img
          src="/images/BG.png"
          alt="Side"
          className="img-fluid"
          style={{ maxHeight: "90%", maxWidth: "90%", objectFit: "contain" }}
        />
      </div>

      {/* ✅ Custom CSS same as SignUp */}
      <style>{`
        .input-icon-wrapper {
          position: relative;
        }
        .input-error-icon {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          height: 20px;
          width: 20px;
          pointer-events: none;
        }
        .input-error {
          padding-right: 2.5rem;
          border-color: #dc3545 !important;
          background-image: none !important;
        }
        .alert-box {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          background-color: #f8d7da;
          color: #842029;
          border: 1px solid #f5c2c7;
          border-radius: 6px;
          font-size: 13px;
          animation: fadeIn 0.3s ease-in-out;
        }
        .alert-box img {
          flex-shrink: 0;
        }
        .close-icon {
          margin-left: auto;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
